/**
 * Client-side Apple Music Error Handler
 * Provides user-friendly error messages for MusicKit JS errors
 */

export const AppleMusicErrors = {
  AUTHORIZATION_ERROR: {
    title: 'Sign In Required',
    message: 'Please sign in to your Apple Music account to continue',
    action: 'Sign In'
  },
  SUBSCRIPTION_REQUIRED: {
    title: 'Subscription Required',
    message: 'This feature requires an active Apple Music subscription',
    action: 'Learn More'
  },
  TOKEN_EXPIRED: {
    title: 'Session Expired',
    message: 'Your session has expired. Please sign in again',
    action: 'Sign In'
  },
  NETWORK_ERROR: {
    title: 'Connection Error',
    message: 'Please check your internet connection and try again',
    action: 'Retry'
  },
  SERVICE_UNAVAILABLE: {
    title: 'Service Unavailable',
    message: 'Apple Music is temporarily unavailable. Please try again later',
    action: 'Retry'
  },
  CONFIGURATION_ERROR: {
    title: 'Setup Error',
    message: 'There was a problem setting up Apple Music',
    action: 'Contact Support'
  },
  HTTPS_REQUIRED: {
    title: 'Secure Connection Required',
    message: 'Apple Music requires a secure connection (HTTPS)',
    action: 'Learn More'
  },
  MEDIA_LICENSE: {
    title: 'Content Unavailable',
    message: 'This content is not available in your region or requires a subscription',
    action: 'Browse Other Songs'
  },
  PLAYBACK_ERROR: {
    title: 'Playback Error',
    message: 'Unable to play this song. Please try another',
    action: 'Skip'
  },
  QUEUE_ERROR: {
    title: 'Queue Error',
    message: 'Unable to update the playback queue',
    action: 'Retry'
  }
};

/**
 * Parse MusicKit JS errors and return user-friendly messages
 */
export function handleMusicKitError(error) {
  console.error('MusicKit Error:', error);
  
  // Map error codes to our error definitions
  const errorMap = {
    'AUTHORIZATION_ERROR': AppleMusicErrors.AUTHORIZATION_ERROR,
    'NETWORK_ERROR': AppleMusicErrors.NETWORK_ERROR,
    'CONFIGURATION_ERROR': AppleMusicErrors.CONFIGURATION_ERROR,
    'MEDIA_LICENSE': AppleMusicErrors.MEDIA_LICENSE,
    'CONTENT_UNAVAILABLE': AppleMusicErrors.MEDIA_LICENSE,
    'SUBSCRIPTION_ERROR': AppleMusicErrors.SUBSCRIPTION_REQUIRED,
    'TOKEN_EXPIRED': AppleMusicErrors.TOKEN_EXPIRED,
    'QUOTA_EXCEEDED': {
      title: 'Rate Limited',
      message: 'Too many requests. Please wait a moment and try again',
      action: 'OK'
    }
  };
  
  // Check for known error codes
  const knownError = errorMap[error.name] || errorMap[error.code];
  if (knownError) {
    return knownError;
  }
  
  // Check error message for patterns
  const errorMessage = error.message?.toLowerCase() || '';
  
  if (errorMessage.includes('https')) {
    return AppleMusicErrors.HTTPS_REQUIRED;
  }
  
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return AppleMusicErrors.NETWORK_ERROR;
  }
  
  if (errorMessage.includes('playback')) {
    return AppleMusicErrors.PLAYBACK_ERROR;
  }
  
  if (errorMessage.includes('queue')) {
    return AppleMusicErrors.QUEUE_ERROR;
  }
  
  if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
    return AppleMusicErrors.AUTHORIZATION_ERROR;
  }
  
  // Default error
  return {
    title: 'Error',
    message: error.message || 'An unexpected error occurred with Apple Music',
    action: 'OK'
  };
}

/**
 * Check if Apple Music is available and properly configured
 */
export async function checkAppleMusicAvailability() {
  if (!window.MusicKit) {
    return {
      available: false,
      reason: 'MusicKit not loaded'
    };
  }
  
  try {
    // Check if MusicKit has been configured before trying to get instance
    if (!window.MusicKit.getInstance) {
      return {
        available: false,
        reason: 'MusicKit not initialized'
      };
    }
    
    const music = window.MusicKit.getInstance();
    
    // Check HTTPS requirement
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      return {
        available: false,
        reason: 'HTTPS required',
        error: AppleMusicErrors.HTTPS_REQUIRED
      };
    }
    
    // Check if configured
    if (!music.isConfigured) {
      return {
        available: false,
        reason: 'Not configured'
      };
    }
    
    // Check capabilities
    const capabilities = {
      canPlayCatalog: music.canPlayCatalog,
      canAddToLibrary: music.canAddToLibrary,
      isAuthorized: music.isAuthorized,
      hasSubscription: music.isAuthorized && music.canPlayCatalog
    };
    
    return {
      available: true,
      capabilities,
      isRestricted: music.isRestricted || false,
      storefrontCountryCode: music.storefrontCountryCode || 'US'
    };
  } catch (error) {
    return {
      available: false,
      reason: 'Configuration error',
      error: handleMusicKitError(error)
    };
  }
}

/**
 * Format error for toast notification
 */
export function formatErrorForToast(error) {
  const parsed = handleMusicKitError(error);
  return `${parsed.title}: ${parsed.message}`;
}